import { EditableText } from '@/components/editable-text';
import { levierColorTokenFor, stepDecorationClasses } from '@/lib/display-tweaks';
import { useTweakByKey } from '@/lib/tweak-store';
import { cn } from '@/lib/utils';
import type { Noyau, SingularityLevier } from '@mss_creator/shared';

export interface SingularitySetupEditorProps {
  leviers: SingularityLevier[];
  noyau: Noyau;
  onChange: (leviers: SingularityLevier[]) => void;
}

type FieldKey = Exclude<keyof SingularityLevier, 'order'>;

const FIELDS: { key: FieldKey; label: string; required?: boolean; widthClass: string }[] = [
  { key: 'driver', label: 'Driver', required: true, widthClass: 'w-48' },
  { key: 'levierImplication', label: "Levier d'implication", widthClass: 'w-40' },
  { key: 'detailImplication', label: "Détail de l'implication", widthClass: 'w-40' },
  { key: 'principeAction', label: "Principe d'action", widthClass: 'w-40' },
  { key: 'methode', label: 'Méthode', widthClass: 'w-40' },
  { key: 'levierValeur', label: 'Levier de valeur', required: true, widthClass: 'w-48' },
  { key: 'detailValeur', label: 'Détail de la valeur', widthClass: 'w-40' },
  { key: 'effetProduit', label: 'Effet produit', widthClass: 'w-40' },
];

/**
 * One row per étape (1-6), one column per field. Cells are click-to-edit
 * (`EditableText`) — no per-row/per-section save button; every commit calls
 * `onChange` with the full 6-row array, and the parent page owns saving.
 */
export function SingularitySetupEditor({ leviers, noyau, onChange }: SingularitySetupEditorProps) {
  // One enum replaces the former singularity-step-accent / -tint boolean pair,
  // which encoded this same four-state choice across two switches.
  const decoration = useTweakByKey('singularity-step-decoration') ?? 'accent';

  function updateCell(order: number, key: FieldKey, value: string) {
    onChange(
      leviers.map((levier) =>
        levier.order === order ? { ...levier, [key]: value || null } : levier,
      ),
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full border-separate border-spacing-0 text-small">
        <thead>
          <tr>
            <th className="w-20 px-2 py-2 text-left font-semibold text-caption text-gray-500">
              Étape
            </th>
            {FIELDS.map(({ key, label, required, widthClass }) => (
              <th
                key={key}
                className={cn(
                  'px-2 py-2 text-left font-semibold text-caption text-gray-500',
                  widthClass,
                )}
              >
                {label}
                {required && <span className="ml-0 text-danger">*</span>}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {leviers.map((levier) => {
            const classes = stepDecorationClasses(
              String(decoration),
              levierColorTokenFor(noyau, levier.order),
            );
            return (
              <tr key={levier.order}>
                <td className={cn(classes.cell, classes.firstCell, 'px-2 py-2 align-top')}>
                  <div className="flex items-center gap-2">
                    {classes.dot && (
                      <span
                        className={cn('h-2 w-2 shrink-0 rounded-full', classes.dot)}
                        aria-hidden="true"
                      />
                    )}
                    <span className="font-semibold text-gray-500">Étape {levier.order}</span>
                  </div>
                </td>
                {FIELDS.map(({ key, widthClass }) => (
                  <td key={key} className={cn(classes.cell, 'px-2 py-2 align-top', widthClass)}>
                    <EditableText
                      value={levier[key] ?? ''}
                      onChange={(value) => updateCell(levier.order, key, value)}
                    />
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
